type BackendErrorBody = {
  success?: boolean;
  message?: string;
  error?: {
    code?: string;
    message?: string;
    correlation_id?: string;
  };
  detail?: string | Array<{ msg?: string } | string>;
};

type AxiosLikeError = {
  isAxiosError?: boolean;
  response?: {
    status?: number;
    data?: BackendErrorBody;
  };
  message?: string;
};

const readBackendBodyMessage = (data: BackendErrorBody | undefined): string | null => {
  if (!data) return null;

  const nested = data.error?.message?.trim();
  if (nested) return nested;

  const topLevel = data.message?.trim();
  if (topLevel) return topLevel;

  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }

  if (Array.isArray(data.detail)) {
    const messages = data.detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          return String(item.msg ?? "");
        }
        return "";
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join(", ");
  }

  return null;
};

const isAxiosLikeError = (error: unknown): error is AxiosLikeError =>
  typeof error === "object" &&
  error !== null &&
  ("isAxiosError" in error || "response" in error);

/** Maps API/axios failures to a user-facing message. */
/** The message the server itself sent in the response body, or null (never a generic transport message). */
export const getApiErrorBodyMessage = (error: unknown): string | null =>
  isAxiosLikeError(error) ? readBackendBodyMessage(error.response?.data) : null;

export const getApiErrorMessage = (
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): string => {
  if (isAxiosLikeError(error)) {
    const fromBody = readBackendBodyMessage(error.response?.data);
    if (fromBody) return fromBody;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};

/**
 * HTTP status of an API failure, or `null` when there is none (network error, thrown bug).
 *
 * Reads both shapes a caller can hold: the raw axios `response.status` (mocks, direct
 * axios use) and the top-level `status` that the `services/api.ts` interceptor puts on
 * every error it rethrows. Every live `apiService` call arrives in the second shape, so
 * reading only the first made status checks like "no retry on 404" dead code in live mode.
 */
export const getApiErrorStatus = (error: unknown): number | null => {
  if (typeof error !== "object" || error === null) return null;
  const { response, status } = error as {
    response?: { status?: unknown };
    status?: unknown;
  };
  const value = response?.status ?? status;
  return typeof value === "number" ? value : null;
};

/**
 * Backend error code (`error.code`, e.g. "TOKEN_EXPIRED") of an API failure, or `null`.
 * Reads the raw axios body and the top-level `code` the `services/api.ts` interceptor adds.
 */
export const getApiErrorCode = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null;
  const { response, code } = error as {
    response?: { data?: BackendErrorBody };
    code?: unknown;
  };
  const value = response?.data?.error?.code ?? code;
  return typeof value === "string" && value ? value : null;
};

/**
 * Per-item `details` of a 422 (e.g. one entry per CSV row that failed validation), or `null`
 * when the failure carries none. Reads the raw axios body (`error.details`, as mocks and direct
 * axios calls hold it) and a top-level `details` the `services/api.ts` interceptor may add.
 * Callers cast to the route's own detail type (e.g. `RowErrorDetailApi[]`).
 */
export const getApiErrorDetails = <T = unknown>(error: unknown): T[] | null => {
  if (typeof error !== "object" || error === null) return null;
  const { response, details } = error as {
    response?: { data?: { error?: { details?: unknown } } };
    details?: unknown;
  };
  const value = response?.data?.error?.details ?? details;
  return Array.isArray(value) && value.length > 0 ? (value as T[]) : null;
};

/**
 * `details` of an API failure when it is an object rather than a list: what a slow create puts
 * there (`{ story_id }`), or a 422 (`{ fields }`). `getApiErrorDetails` keeps only the list form.
 * Reads the same two shapes: the raw axios body and the interceptor's top-level `details`.
 */
export const getApiErrorDetailsRecord = (
  error: unknown,
): Record<string, unknown> | null => {
  if (typeof error !== "object" || error === null) return null;
  const { response, details } = error as {
    response?: { data?: { error?: { details?: unknown } } };
    details?: unknown;
  };
  const value = response?.data?.error?.details ?? details;
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
};

/**
 * React Query `retry` predicate: up to `maxRetries` attempts, never on a 404 — a missing
 * resource will not appear on retry, so the error shows at once.
 */
export const retryUnlessNotFound =
  (maxRetries: number) =>
  (failureCount: number, error: unknown): boolean =>
    getApiErrorStatus(error) !== 404 && failureCount < maxRetries;
